/* useVoiceSession — the realtime voice engine, extracted from VoiceAgent so
   the console copilot dock and the standalone voice widget share ONE
   implementation.

   Proven plumbing kept verbatim: PCM16 ↔ Float32 conversion, scheduled play
   queue, realtime WebSocket lifecycle (session.update handshake, VAD events,
   barge-in cancel, transcript deltas, tool-call loop), frequency-bar
   visualiser via an AnalyserNode.

   The flow:
     1. POST {API}/voice/session (Firebase auth) → ephemeral token +
        wsUrl/wsProtocols + session config. The provider API key never
        reaches this code.
     2. Open a WebSocket DIRECTLY to the provider; stream mic PCM up, play
        PCM deltas back.
     3. Tool calls: the caller's onClientTool(name, args) runs first (client
        tools like navigate_app — the caller has the router); anything it
        doesn't handle POSTs to {API}/voice/tool.

   New over the original: sendText(text) injects a TYPED user message into
   the live conversation (conversation.item.create + response.create), so a
   voice session doubles as a chat session. */
import { useState, useEffect, useRef, useCallback } from 'react';
import firebase from '../firebaseConfig';

import { API_BASE } from '../data/env';
import { captureScreenOutline, screenContextItem } from '../utils/screenOutline';
import { DRIVING_TOOLS } from '../utils/pageControls';

// The voice prompt gets a shorter outline than a chat turn: it is re-sent on
// every route change, at speech start and after the agent drives the page,
// and the realtime model reads system items aloud less gracefully than the
// chat model. Handles ([b2]) ride inside this budget.
const VOICE_SCREEN_CHARS = 1500;
// Speech-start refreshes no more often than this (a quick back-and-forth
// would otherwise push an outline per sentence).
const SCREEN_MIN_GAP_MS = 4000;

const API = API_BASE;
const SAMPLE_RATE = 24000;
export const NUM_BARS = 20;
const ATTACK = 0.12;
const DECAY = 0.45;

// Shown the instant a tool starts, before the round trip. Keep these short
// enough to sit on the voice button. A question or route, when we already
// have it, is the difference between a frozen "Working" and proof of progress.
function activityForTool(name, args = {}) {
  const hint = String(args.question || args.query || args.path || args.target || args.request || '')
    .replace(/\s+/g, ' ').trim().slice(0, 72);
  let base;
  switch (name) {
    case 'ask_knowledge':
    case 'search_knowledge':
    case 'knowledge_doc':
      base = 'Looking that up';
      break;
    case 'navigate_app':
      base = 'Opening that page';
      break;
    case 'scroll_page':
      base = 'Moving down the page';
      break;
    case 'zoom_into':
    case 'set_zoom':
      base = 'Zooming in';
      break;
    case 'click_element':
      base = 'Clicking that';
      break;
    case 'type_text':
    case 'set_control':
      base = 'Setting that';
      break;
    case 'find_on_page':
      base = 'Finding that on the page';
      break;
    case 'read_element':
      base = 'Reading that';
      break;
    case 'highlight_element':
    case 'hover_element':
      base = 'Pointing at it';
      break;
    case 'console_lookup':
      base = 'Checking your account';
      break;
    case 'delegate_to_copilot':
      base = 'Handing this to the builder';
      break;
    default:
      base = 'Working on that';
  }
  return hint ? `${base} — ${hint}` : base;
}

function float32ToBase64PCM16(f32) {
  const pcm16 = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm16.buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

async function authHeaders() {
  // Signed-out visitors get an anonymous session on first use — the backend
  // still verifies a real ID token and limits them to public tools.
  let user = firebase.auth().currentUser;
  if (!user) {
    try { ({ user } = await firebase.auth().signInAnonymously()); } catch { /* the caller surfaces the 401 */ }
  }
  const token = user ? await user.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// `context` = { orgId, page }: where the user is. Sent with the session
// mint and every server tool call (console_lookup needs the org), and pushed
// into a live session as a system item when the route changes so the model
// knows what "this app" / "here" means without being asked.
export default function useVoiceSession({ onClientTool, context = null } = {}) {
  const [messages, setMessages] = useState([]);
  const [active, setActive] = useState(false);
  const [state, setState] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // Plain sentence for the gap between "I heard you" and the first spoken
  // word. A lookup can take many seconds; "Thinking" alone reads as frozen.
  const [activity, setActivity] = useState('');
  const [activitySecs, setActivitySecs] = useState(0);
  // True once the provider has accepted session.update — typed text waits
  // for this instead of falling through to the silent text endpoint.
  const [ready, setReady] = useState(false);
  // Sources from the lookup in flight, shown before the spoken answer lands.
  const [pendingCitations, setPendingCitations] = useState([]);
  // The think events of the turn in flight — each /voice/tool trace appends
  // its stream as it returns, so the dock can play the voice turn live the
  // way it plays a streamed text turn. Cleared when the answer commits.
  const [liveThink, setLiveThink] = useState([]);

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const captureCtxRef = useRef(null);
  const playCtxRef = useRef(null);
  const playGainRef = useRef(null);
  const processorRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const barsRef = useRef(null);
  const prevLevelsRef = useRef(new Float32Array(NUM_BARS));
  const sessionReadyRef = useRef(false);
  const micBufferRef = useRef([]);
  const playQueueRef = useRef([]);
  const nextPlayTimeRef = useRef(0);
  const currentSourceRef = useRef(null);
  const activeResponseIdRef = useRef(null);
  const responseHadToolCallsRef = useRef(false);
  // One response at a time. Tool handlers are async (a delegation posts to
  // the backend), so a follow-up response.create fired on response.done
  // raced ahead of the function_call_output; the model then answered
  // without the result and re-issued the call — up to sixty delegations in
  // one turn before the provider closed the socket. Every response.create
  // now goes through requestResponse(): it waits for in-flight tool outputs
  // and for the active response, and collapses duplicates into one.
  const pendingToolsRef = useRef(0);
  const wantResponseRef = useRef(false);
  const requestResponseRef = useRef(() => {});
  const agentPartialRef = useRef('');
  const abortedRef = useRef(false);
  const stateRef = useRef('idle');
  const sessionStartRef = useRef(0);
  const elapsedTimerRef = useRef(null);
  const onClientToolRef = useRef(onClientTool);
  const contextRef = useRef(context);
  const speakerMutedRef = useRef(false);
  const volumeRef = useRef(1);
  const liveRef = useRef(false);
  const activityRef = useRef('');
  const activityAtRef = useRef(0);
  const citeRef = useRef([]);
  const sawTranscriptRef = useRef(false);

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { onClientToolRef.current = onClientTool; }, [onClientTool]);
  const ctxOrgId = context?.orgId || '';
  const ctxPage = context?.page || '';
  /* ── Screen refresh into the LIVE session ──────────────────────────
     The model's picture of the page is a system item (screenOutline →
     screenContextItem): sent when the route changes, when the user starts
     speaking (≥ SCREEN_MIN_GAP_MS apart), and 600 ms after the agent drives
     the page (navigate / scroll / zoom / click …) so the next sentence
     describes the screen it just changed, not the one before. Identical
     outlines are not re-sent. No response.create — context, not a turn. */
  const lastScreenRef = useRef({ text: '', at: 0 });
  const sendScreen = useCallback((reason = 'route') => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) return false;
    const now = Date.now();
    if (reason === 'speech' && now - lastScreenRef.current.at < SCREEN_MIN_GAP_MS) return false;
    try {
      const outline = captureScreenOutline({ maxChars: VOICE_SCREEN_CHARS });
      if (!outline || outline === lastScreenRef.current.text) return false;
      const { page, orgId } = contextRef.current || {};
      const text = screenContextItem(outline, orgId ? `organization ${orgId}` : '', page || window.location.pathname);
      ws.send(JSON.stringify({
        type: 'conversation.item.create',
        item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] },
      }));
      lastScreenRef.current = { text: outline, at: now };
      return true;
    } catch (_) { return false; }
  }, []);

  useEffect(() => {
    contextRef.current = { orgId: ctxOrgId, page: ctxPage };
    // Live session: tell the model where the user is now (debounced so the
    // new route renders before the outline is captured).
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current || !ctxPage) return undefined;
    const t = setTimeout(() => sendScreen('route'), 400);
    return () => clearTimeout(t);
  }, [ctxOrgId, ctxPage, sendScreen]);

  // One label per phase. Repeating the same label must not reset the clock
  // — a lookup that takes 8 seconds should count, not blink back to 0.
  const noteActivity = useCallback((label) => {
    if (!label || activityRef.current === label) return;
    activityRef.current = label;
    activityAtRef.current = Date.now();
    setActivity(label);
    setActivitySecs(0);
  }, []);
  const clearActivity = useCallback(() => {
    activityRef.current = '';
    activityAtRef.current = 0;
    setActivity('');
    setActivitySecs(0);
  }, []);
  useEffect(() => {
    if (!activity) return undefined;
    const id = setInterval(() => {
      const at = activityAtRef.current;
      setActivitySecs(at ? Math.max(0, Math.round((Date.now() - at) / 1000)) : 0);
    }, 1000);
    return () => clearInterval(id);
  }, [activity]);

  const commitAssistant = useCallback((text) => {
    const clean = String(text || '').trim();
    agentPartialRef.current = '';
    setStreamingText('');
    if (!clean) return;
    const citations = citeRef.current.splice(0);
    const trace = traceRef.current;
    traceRef.current = null;
    setPendingCitations([]);
    setLiveThink([]);
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant' && last.content === clean) return prev;
      return [...prev, {
        id: `a-${Date.now()}`,
        at: Date.now(),
        role: 'assistant',
        content: clean,
        ...(citations.length ? { citations } : {}),
        ...(trace ? { trace } : {}),
      }];
    });
  }, []);

  // The retrieval trace of a live voice turn: /voice/tool returns one per
  // knowledge call (with its think stream — the ordered search events the
  // BrainThink player replays); several calls in one turn concatenate their
  // events. Committed onto the assistant message, never sent to the model.
  const traceRef = useRef(null);
  const noteTrace = (trace) => {
    if (!trace) return;
    const prev = traceRef.current;
    const events = [...(prev?.think?.events || []), ...(trace.think?.events || [])].map((e, i) => ({ ...e, seq: i + 1 }));
    const base = trace.hits?.length || !prev ? trace : prev;
    traceRef.current = { ...base, ...(events.length ? { think: { v: 1, events, total: events.length, counts: {} } } : {}) };
    if (events.length) setLiveThink(events);
  };

  const appendPartial = useCallback((delta) => {
    const piece = String(delta || '');
    if (!piece || !activeResponseIdRef.current) return;
    agentPartialRef.current += piece;
    setStreamingText(agentPartialRef.current);
    clearActivity();
  }, [clearActivity]);

  /* ── Tool dispatch: client handler first, then backend allowlist ── */
  // A client tool ran in the browser: tell the server (audit row, and for a
  // navigation / page control a one-event think trace so the stage draws
  // the move). Fire-and-forget — never on the model's path.
  const reportClientTool = useCallback((name, args, local) => {
    const outcome = local?.error ? `error: ${String(local.error).slice(0, 200)}` : (local?.navigatedTo ? `→ ${local.navigatedTo}` : 'ok');
    const page = `${window.location.pathname}${window.location.search || ''}`.slice(0, 300);
    authHeaders().then((h) => fetch(`${API}/voice/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...h },
      body: JSON.stringify({ kind: 'tool', tool: name, params: args || {}, outcome, page }),
    })).then((r) => (r.ok ? r.json() : null)).then((d) => {
      // Only while the turn is still forming — a late reply must not re-open
      // a stage the committed answer already closed.
      if (d?.trace && activeResponseIdRef.current) noteTrace(d.trace);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const executeTool = useCallback(async (name, args) => {
    try {
      const local = onClientToolRef.current && (await onClientToolRef.current(name, args));
      if (local !== undefined && local !== null) { reportClientTool(name, args, local); return local; }
    } catch (err) {
      reportClientTool(name, args, { error: err.message });
      return { error: err.message };
    }
    try {
      const res = await fetch(`${API}/voice/tool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ tool: name, params: args, context: contextRef.current || {} }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || `tool failed (${res.status})` };
      noteTrace(data.trace);
      return data.result;
    } catch (err) {
      return { error: err.message };
    }
  }, [reportClientTool]);

  /* ── Audio playback (scheduled PCM queue + gain for speaker mute/volume) ── */
  const applyOutputGain = useCallback(() => {
    if (playGainRef.current) {
      playGainRef.current.gain.value = speakerMutedRef.current ? 0 : volumeRef.current;
    }
  }, []);

  const stopPlayback = useCallback(() => {
    playQueueRef.current = [];
    if (playGainRef.current && playCtxRef.current) {
      try { playGainRef.current.disconnect(); } catch (_) {}
      const gain = playCtxRef.current.createGain();
      gain.connect(playCtxRef.current.destination);
      playGainRef.current = gain;
      applyOutputGain();
    }
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch (_) {}
      currentSourceRef.current = null;
    }
    nextPlayTimeRef.current = 0;
  }, []);

  const drainPlayQueue = useCallback(() => {
    if (!playCtxRef.current || playQueueRef.current.length === 0) return;
    const ctx = playCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const dest = playGainRef.current || ctx.destination;
    while (playQueueRef.current.length > 0) {
      const chunk = playQueueRef.current.shift();
      const buffer = ctx.createBuffer(1, chunk.length, SAMPLE_RATE);
      buffer.getChannelData(0).set(chunk);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(dest);
      const startAt = Math.max(ctx.currentTime, nextPlayTimeRef.current);
      source.start(startAt);
      nextPlayTimeRef.current = startAt + buffer.duration;
      currentSourceRef.current = source;
    }
  }, []);

  const playBase64Audio = useCallback((base64Data) => {
    try {
      const binStr = atob(base64Data);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const pcm16 = new Int16Array(bytes.buffer);
      const f32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) f32[i] = pcm16[i] / 32768.0;
      playQueueRef.current.push(f32);
      drainPlayQueue();
    } catch (err) {
      console.warn('[voice] audio decode error:', err.message);
    }
  }, [drainPlayQueue]);

  /* ── Visualiser ───────────────────────────────────────────────── */
  const startVisualiser = useCallback((ctx, micStream) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { analyserRef.current && analyserRef.current.disconnect(); } catch (_) {}
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.3;
    ctx.createMediaStreamSource(micStream).connect(analyser);
    analyserRef.current = analyser;
    const bins = analyser.frequencyBinCount;
    const data = new Uint8Array(bins);
    const prev = prevLevelsRef.current;
    let t = 0;
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const isSpeaking = stateRef.current === 'speaking';
      t += 0.08;
      const step = Math.max(1, Math.floor(bins / NUM_BARS));
      const bars = barsRef.current?.children;
      for (let i = 0; i < NUM_BARS; i++) {
        const idx = Math.min(i * step, bins - 1);
        let raw = data[idx] / 255;
        if (isSpeaking) {
          const wave = 0.3 + 0.25 * Math.sin(t + i * 0.35) + 0.15 * Math.sin(t * 1.7 + i * 0.2) + 0.1 * Math.random();
          raw = Math.max(raw, wave);
        }
        const smooth = raw > prev[i] ? ATTACK : DECAY;
        const val = prev[i] * smooth + raw * (1 - smooth);
        prev[i] = val;
        if (bars && bars[i]) bars[i].style.height = `${Math.max(2, val * 20)}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopVisualiser = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { analyserRef.current && analyserRef.current.disconnect(); } catch (_) {}
    analyserRef.current = null;
    prevLevelsRef.current = new Float32Array(NUM_BARS);
    const bars = barsRef.current?.children;
    if (bars) for (let i = 0; i < bars.length; i++) bars[i].style.height = '2px';
  }, []);

  /* ── Session lifecycle ────────────────────────────────────────── */
  const stop = useCallback(() => {
    abortedRef.current = true;
    stopVisualiser();
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    if (processorRef.current) { try { processorRef.current.disconnect(); } catch (_) {} processorRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} wsRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (captureCtxRef.current) { captureCtxRef.current.close().catch(() => {}); captureCtxRef.current = null; }
    if (playGainRef.current) { try { playGainRef.current.disconnect(); } catch (_) {} playGainRef.current = null; }
    if (playCtxRef.current) { playCtxRef.current.close().catch(() => {}); playCtxRef.current = null; }
    sessionReadyRef.current = false;
    liveRef.current = false;
    activeResponseIdRef.current = null;
    micBufferRef.current = [];
    playQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    citeRef.current = [];
    setActive(false);
    setReady(false);
    setState('idle');
    stateRef.current = 'idle';
    setMuted(false);
    setStreamingText('');
    setPendingCitations([]);
    activityRef.current = '';
    activityAtRef.current = 0;
    setActivity('');
    setActivitySecs(0);
    setElapsed(0);
    sessionStartRef.current = 0;
    agentPartialRef.current = '';
  }, [stopVisualiser]);

  const start = useCallback(async () => {
    if (liveRef.current) return;
    liveRef.current = true;
    abortedRef.current = false;
    sessionReadyRef.current = false;
    micBufferRef.current = [];
    playQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    // Spoken answer is the default. A mute from an earlier session does not
    // carry into the next one — the form over the film opens ready to talk.
    speakerMutedRef.current = false;
    setSpeakerMuted(false);
    setError(null);
    setReady(false);
    setState('connecting');
    stateRef.current = 'connecting';
    setActive(true);
    noteActivity('Connecting');
    try {
      // Unlock playback inside the click that started voice. Creating the
      // context after getUserMedia returns leaves it suspended, so the
      // speaker looks on and stays silent.
      const AC = window.AudioContext || window.webkitAudioContext;
      const playCtx = new AC({ sampleRate: SAMPLE_RATE });
      playCtxRef.current = playCtx;
      const gain = playCtx.createGain();
      gain.connect(playCtx.destination);
      playGainRef.current = gain;
      applyOutputGain();
      playCtx.resume().catch(() => {});

      const sessionFetch = (async () => {
        const r = await fetch(`${API}/voice/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({
            page: contextRef.current?.page || `${window.location.pathname}${window.location.search || ''}`,
            orgId: contextRef.current?.orgId || '',
            screen: (() => { const o = captureScreenOutline({ maxChars: VOICE_SCREEN_CHARS }); lastScreenRef.current = { text: o, at: Date.now() }; return o; })(),
          }),
        });
        if (!r.ok) {
          let detail = '';
          try { detail = (await r.json()).error || ''; } catch (_) {}
          throw new Error(detail || `session failed (${r.status})`);
        }
        return r.json();
      })();
      const [micStream, sessionData] = await Promise.all([
        navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        }),
        sessionFetch,
      ]);
      if (abortedRef.current || !playCtxRef.current) { micStream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = micStream;
      const { ephemeralToken, wsUrl, wsProtocols, sessionConfig } = sessionData;
      if (!ephemeralToken) throw new Error('no ephemeral token from /voice/session');

      const captureCtx = new AC({ sampleRate: SAMPLE_RATE });
      captureCtxRef.current = captureCtx;

      const source = captureCtx.createMediaStreamSource(micStream);
      const processor = captureCtx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      source.connect(processor);
      processor.connect(captureCtx.destination);
      processor.onaudioprocess = (e) => {
        const f32 = new Float32Array(e.inputBuffer.getChannelData(0));
        if (!sessionReadyRef.current) { micBufferRef.current.push(f32); return; }
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: float32ToBase64PCM16(f32) }));
      };

      startVisualiser(captureCtx, micStream);
      sessionStartRef.current = Date.now();
      setElapsed(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - sessionStartRef.current) / 1000));
      }, 1000);

      const ws = new WebSocket(wsUrl, wsProtocols);
      wsRef.current = ws;
      ws.onopen = () => {
        try { ws.send(JSON.stringify({ type: 'session.update', session: sessionConfig })); } catch (_) {}
      };
      ws.onmessage = async (e) => {
        let evt;
        try { evt = JSON.parse(e.data); } catch { return; }
        switch (evt.type) {
          case 'session.updated':
            sessionReadyRef.current = true;
            setReady(true);
            setState('listening'); stateRef.current = 'listening';
            if (activityRef.current === 'Connecting') clearActivity();
            // The page may have moved on since the mint captured it.
            setTimeout(() => sendScreen('session'), 300);
            if (micBufferRef.current.length > 0 && ws.readyState === WebSocket.OPEN) {
              micBufferRef.current.forEach((chunk) => {
                ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: float32ToBase64PCM16(chunk) }));
              });
              micBufferRef.current = [];
            }
            break;
          case 'error':
            setError((evt.error && (evt.error.message || evt.error.code)) || 'Voice service error');
            break;
          case 'input_audio_buffer.speech_started':
            // Barge-in: stop playback and cancel the in-flight response.
            // The page hears it too (a film the pitch is playing pauses).
            stopPlayback();
            try { window.dispatchEvent(new CustomEvent('nv:voice-speech-started')); } catch (_) {}
            sendScreen('speech');
            agentPartialRef.current = '';
            sawTranscriptRef.current = false;
            setStreamingText('');
            setState('listening'); stateRef.current = 'listening';
            clearActivity();
            if (activeResponseIdRef.current && ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: 'response.cancel' })); } catch (_) {}
            }
            activeResponseIdRef.current = null;
            break;
          case 'input_audio_buffer.speech_stopped':
            setState('processing'); stateRef.current = 'processing';
            noteActivity('Working on that');
            break;
          case 'response.created':
            if (agentPartialRef.current) commitAssistant(agentPartialRef.current);
            activeResponseIdRef.current = evt.response?.id || true;
            responseHadToolCallsRef.current = false;
            sawTranscriptRef.current = false;
            if (!activityRef.current || activityRef.current === 'Working on that') noteActivity('Thinking');
            break;
          case 'response.output_item.added': {
            const item = evt.item || {};
            if (item.type === 'function_call' && item.name) {
              responseHadToolCallsRef.current = true;
              noteActivity(activityForTool(item.name));
            }
            break;
          }
          case 'response.output_audio.delta':
          case 'response.audio.delta':
            if (!activeResponseIdRef.current) break;
            setState('speaking'); stateRef.current = 'speaking';
            // Keep a visible phase until words arrive. Clearing here made a
            // muted speaker look frozen for the whole generation.
            if (!agentPartialRef.current) noteActivity('Speaking');
            if (evt.delta) playBase64Audio(evt.delta);
            break;
          case 'response.output_audio_transcript.delta':
          case 'response.audio_transcript.delta':
            sawTranscriptRef.current = true;
            appendPartial(evt.delta);
            break;
          case 'response.output_text.delta':
          case 'response.text.delta':
            if (sawTranscriptRef.current) break;
            appendPartial(evt.delta || evt.text);
            break;
          case 'response.output_audio_transcript.done':
          case 'response.audio_transcript.done':
            sawTranscriptRef.current = true;
            commitAssistant(evt.transcript || agentPartialRef.current);
            break;
          case 'response.output_text.done':
          case 'response.text.done':
            // Text-modality replies (typed messages). Skip when the spoken
            // transcript already committed the same turn.
            if (sawTranscriptRef.current) break;
            commitAssistant(evt.text || agentPartialRef.current);
            break;
          case 'response.done': {
            activeResponseIdRef.current = null;
            if (!agentPartialRef.current) setStreamingText('');
            if ((responseHadToolCallsRef.current || wantResponseRef.current) && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              // Tool outputs were (or are being) appended, or a note asked
              // for a reply while this response was speaking — one follow-up
              // response once the outputs are in and the audio has drained.
              responseHadToolCallsRef.current = false;
              setState('thinking'); stateRef.current = 'thinking';
              noteActivity('Writing the answer');
              requestResponseRef.current();
            } else {
              setState('listening'); stateRef.current = 'listening';
              if (agentPartialRef.current) commitAssistant(agentPartialRef.current);
              else clearActivity();
            }
            break;
          }
          case 'response.cancelled':
            stopPlayback();
            activeResponseIdRef.current = null;
            break;
          case 'conversation.item.input_audio_transcription.completed': {
            const userText = (evt.transcript || '').trim();
            if (userText) {
              // The provider re-sends one utterance as it grows (same
              // item_id, or a transcript that extends the last one): that is
              // ONE user line, replaced in place — not four copies of
              // increasing length in the transcript the planner reads.
              const itemId = evt.item_id || null;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                const extends_ = last?.role === 'user' && (
                  (itemId && last.itemId === itemId)
                  || (userText.startsWith(last.content) && userText !== last.content)
                );
                const line = { id: extends_ ? last.id : `u-${Date.now()}`, at: extends_ ? last.at : Date.now(), itemId, role: 'user', content: userText };
                return extends_ ? [...prev.slice(0, -1), line] : [...prev, line];
              });
            }
            break;
          }
          case 'response.function_call_arguments.done': {
            const fnName = evt.name;
            const callId = evt.call_id;
            let argsObj = {};
            try { argsObj = evt.arguments ? JSON.parse(evt.arguments) : {}; } catch (_) {}
            responseHadToolCallsRef.current = true;
            pendingToolsRef.current += 1;
            noteActivity(activityForTool(fnName, argsObj));
            let result;
            try { result = await executeTool(fnName, argsObj); } finally { pendingToolsRef.current = Math.max(0, pendingToolsRef.current - 1); }
            if (Array.isArray(result?.citations) && result.citations.length) {
              const seen = new Set(citeRef.current.map((c) => c.docId || c.ref));
              for (const c of result.citations) {
                const key = c.docId || c.ref;
                if (seen.has(key)) continue;
                seen.add(key);
                citeRef.current.push(c);
              }
              setPendingCitations(citeRef.current.slice());
            }
            noteActivity('Writing the answer');
            try {
              if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && callId) {
                wsRef.current.send(JSON.stringify({
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: callId,
                    output: JSON.stringify(result || { ok: true }),
                  },
                }));
              }
            } catch (_) {}
            // The agent moved the page: refresh its picture once the smooth
            // scroll / route / lens has settled, before it narrates.
            if (DRIVING_TOOLS.has(fnName)) setTimeout(() => sendScreen('after-tool'), 600);
            break;
          }
          default:
            break;
        }
      };
      ws.onerror = () => setError('Voice connection error');
      ws.onclose = (evt) => {
        if (!abortedRef.current) {
          if (evt && evt.code !== 1000) setError(`Voice ended (${evt.code}${evt.reason ? `: ${evt.reason}` : ''})`);
          stop();
        }
      };
    } catch (err) {
      console.error('[voice] start failed:', err);
      setError(err && err.name === 'NotAllowedError' ? 'Microphone permission denied.' : err.message || 'Failed to start voice');
      stop();
    }
  }, [executeTool, startVisualiser, stopPlayback, playBase64Audio, stop, noteActivity, clearActivity, appendPartial, commitAssistant, applyOutputGain, sendScreen]);

  const toggleMute = useCallback(() => {
    if (!streamRef.current) return;
    const track = streamRef.current.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMuted(!track.enabled);
  }, []);

  const toggleSpeaker = useCallback(() => {
    speakerMutedRef.current = !speakerMutedRef.current;
    setSpeakerMuted(speakerMutedRef.current);
    applyOutputGain();
  }, [applyOutputGain]);

  const unmuteSpeaker = useCallback(() => {
    speakerMutedRef.current = false;
    setSpeakerMuted(false);
    applyOutputGain();
    const ctx = playCtxRef.current;
    if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
  }, [applyOutputGain]);

  const changeVolume = useCallback((v) => {
    const vol = Math.max(0, Math.min(1, Number(v) || 0));
    volumeRef.current = vol;
    setVolume(vol);
    applyOutputGain();
  }, [applyOutputGain]);

  /* Ask the model for a response — exactly one, once it is safe: no
     response in flight (response.done calls back in), every tool output
     landed, the audio queue drained. Several asks collapse into one. */
  const requestResponse = useCallback(() => {
    wantResponseRef.current = true;
    const tick = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) { wantResponseRef.current = false; return; }
      if (!wantResponseRef.current) return;
      const active = activeResponseIdRef.current;
      // Optimistic marker from our own send that never got response.created
      // (provider rejected it) must not wedge the session.
      if (active && typeof active === 'number' && Date.now() - active > 15000) activeResponseIdRef.current = null;
      else if (active) return; // response.done re-enters
      if (pendingToolsRef.current > 0) { setTimeout(tick, 120); return; }
      const ctx = playCtxRef.current;
      const drainMs = ctx ? Math.max(0, (nextPlayTimeRef.current - ctx.currentTime) * 1000) : 0;
      if (drainMs > 60) { setTimeout(tick, Math.min(drainMs, 400)); return; }
      wantResponseRef.current = false;
      activeResponseIdRef.current = Date.now();
      try { ws.send(JSON.stringify({ type: 'response.create' })); } catch (_) {}
    };
    tick();
  }, []);
  useEffect(() => { requestResponseRef.current = requestResponse; }, [requestResponse]);

  /* Out-of-band fact for the LIVE conversation (a plan is waiting, a turn
     finished, an error) — a system item; with respond:true the agent
     speaks to it as soon as the current response is done. Returns false
     when no live session. `role: 'user'` sends it as a hidden user turn
     (never shown in the transcript) for cues the model must act on — a
     system item after a terse assistant turn tends to be answered by
     repeating that turn (the film-ended note got "Here it is." back). */
  const sendSystem = useCallback((text, { respond = false, role = 'system' } = {}) => {
    const ws = wsRef.current;
    const content = String(text || '').trim();
    if (!content || !ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) return false;
    try {
      ws.send(JSON.stringify({
        type: 'conversation.item.create',
        item: { type: 'message', role: role === 'user' ? 'user' : 'system', content: [{ type: 'input_text', text: content.slice(0, 4000) }] },
      }));
      if (respond) requestResponse();
      return true;
    } catch (_) {
      return false;
    }
  }, [requestResponse]);

  /* Typed message into the LIVE conversation — the agent hears it exactly
     like speech (tools included) and replies with voice. */
  const sendText = useCallback((text) => {
    const ws = wsRef.current;
    const content = String(text || '').trim();
    if (!content || !ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) return false;
    try {
      ws.send(JSON.stringify({
        type: 'conversation.item.create',
        item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: content }] },
      }));
      // A user turn supersedes any follow-up a note asked for (barge-in).
      wantResponseRef.current = false;
      activeResponseIdRef.current = Date.now();
      ws.send(JSON.stringify({ type: 'response.create' }));
      setMessages((prev) => [...prev, { id: `u-${Date.now()}`, at: Date.now(), role: 'user', content }]);
      setState('processing'); stateRef.current = 'processing';
      noteActivity('Working on that');
      return true;
    } catch (_) {
      return false;
    }
  }, [noteActivity]);

  useEffect(() => () => stop(), [stop]);

  const clearMessages = useCallback(() => setMessages([]), []);

  // How far ahead of the audible playhead the provider is, in ms. Visual
  // tools (navigate, scroll) defer by this so the page moves when the
  // sentence that triggered them is heard.
  const isLive = useCallback(() => liveRef.current, []);

  const playbackLagMs = useCallback(() => {
    const ctx = playCtxRef.current;
    if (!ctx) return 0;
    return Math.min(20000, Math.max(0, (nextPlayTimeRef.current - ctx.currentTime) * 1000));
  }, []);

  return {
    configured: !!API,
    active, ready, state, muted, speakerMuted, volume, error, elapsed, streamingText, messages, barsRef,
    start, stop, toggleMute, toggleSpeaker, unmuteSpeaker, changeVolume, sendText, sendSystem, sendScreen, clearMessages, playbackLagMs, isLive,
    activity, activitySecs, pendingCitations, liveThink,
  };
}
